import Phaser from 'phaser';
import { TILE_SIZE, VIEW_TILES_X, VIEW_TILES_Y, WORLD_TILES, MAP_BORDER_SIZE, GAME_FONT_FAMILY } from '@shared/constants';
import type { MapData, MapLightData, MapTransladoData, TileData, MapAreaData, MapRect, MapFishingSpot } from '@shared/types/map';
import { FISH_SPECIES } from '@shared/data/fish';
import { Direction } from '@shared/types/entity';
import { isDecorationKey, getDecorationAssetUrl } from '../util/decorationAssets';
import { HOUSES, stampHouse } from '@shared/world/houseData';
import {
  preloadBodySprites,
  setupBodySpritesheets,
  createBodyAnimations,
  getBodyAnimKey,
  getBodyFrameDimensions,
  getBodyIdleFrame,
  getBodySpriteKey,
  getBodyCenterOffset,
  WALK_ANIM_FRAME_RATE,
} from '../sprites/BodySpriteManager';
import { preloadHeadSprites, getHeadSpriteKey, getHeadFrameForDirection } from '../sprites/HeadSpriteManager';
import {
  preloadShieldSprites,
  getShieldSpriteKey,
  getShieldIdleFrame,
} from '../sprites/ShieldSpriteManager';
import {
  preloadWeaponSprites,
  getWeaponSpriteKey,
  getWeaponIdleFrame,
} from '../sprites/WeaponSpriteManager';
import {
  preloadSurfaceAtlases,
  setupSurfaceTextures,
  getSurfaceTextureKey,
  getSurfaceIndex,
  getSurfacesByType,
  getSurfacesByTypeAndSubtype,
} from '../sprites/SurfaceSpriteManager';
import type { SurfaceDefinition, SurfaceIndex } from '@shared/types/surface';
import { getNPCTemplate, getAllNPCTemplates } from '@shared/data/npcs';
import {
  preloadWaterFrames,
  getWaterTypeForSprite,
  getWaterFrameTextureKey,
  getWaterFrameCount,
  WATER_FRAME_MS,
} from '../sprites/WaterSpriteManager';

interface MapEditorConfig {
  mapData?: MapData;
}

export type MapEditorTileSelectCallback = (x: number, y: number) => void;

/**
 * Escena de editor de mapas offline.
 * Renderiza un MapData usando la misma lógica visual que GameScene (tiles, decoraciones, profundidades),
 * pero sin conexión al servidor ni entidades de jugador.
 */
export class MapEditorScene extends Phaser.Scene {
  public static onTileSelected: MapEditorTileSelectCallback | null = null;
  public static onSurfaceDeselected: (() => void) | null = null; // Callback cuando se deselecciona una superficie
  public static npcPlacements: Array<{ templateId: string; x: number; y: number }> = [];
  public static itemPlacements: Array<{ itemId: string; x: number; y: number; quantity: number }> = [];
  public static lightPlacements: MapLightData[] = []; // Luces del mapa
  public static transladoPlacements: MapTransladoData[] = []; // Translados del mapa
  public static areaPlacements: MapAreaData[] = []; // Áreas del mapa
  public static fishingSpotPlacements: MapFishingSpot[] = []; // Cardúmenes de pesca del mapa
  private fishSpotSprites: Map<string, { rect: Phaser.GameObjects.Rectangle; label: Phaser.GameObjects.Text }> = new Map();
  private showFishingSpots = true;
  /** Callback al terminar de dibujar un rectángulo en modo área (lo consume AuthUI). */
  public static onAreaRectDrawn: ((rect: MapRect) => void) | null = null;
  // La setea DevToolsLauncher (MapEditorScene.currentMapData = ...) ANTES de crear el juego del
  // editor, tras fetchear map1 en runtime. Definite-assignment: nunca se lee antes de asignarse.
  public static currentMapData!: MapData;
  // Señal para que el minimapa (AuthUI) re-hornee su terreno: se activa al cargar
  // o editar el mapa. Evita que el minimapa itere ~390k tiles en cada frame.
  public static minimapDirty: boolean = true;

  private configData: Required<MapEditorConfig>;

  private currentMap: MapData | null = null;
  private decorationIndex: Record<string, { name: string; type: string }> | null = null;
  private surfaceIndex: SurfaceIndex | null = null;
  private blockedTiles: Set<string> | null = null;
  private borderLimitTiles: Set<string> | null = null; // Índice separado para border_limit
  private blockedTileGraphics: Phaser.GameObjects.Graphics | null = null;
  private borderLimitGraphics: Phaser.GameObjects.Graphics | null = null;
  private showBlockedTiles: boolean = true;
  private showMapBorders: boolean = false; // Bloqueos de mapa (border_limit)
  private showGrid: boolean = false;
  private gridGraphics: Phaser.GameObjects.Graphics | null = null;
  private lastGridTileStartX: number | null = null;
  private lastGridTileStartY: number | null = null;

  private selectedTileX: number = 0;
  private selectedTileY: number = 0;
  private selectedTileRect: Phaser.GameObjects.Rectangle | null = null;
  private tooltip: Phaser.GameObjects.Container | null = null;
  private isPointerDown: boolean = false;
  private isDragging: boolean = false;
  private dragStartX: number = 0;
  private dragStartY: number = 0;
  private dragStartScrollX: number = 0;
  private dragStartScrollY: number = 0;
  private fpsText: Phaser.GameObjects.Text | null = null;

  private cursors: Phaser.Types.Input.Keyboard.CursorKeys | null = null;
  
  // Sprites de NPCs e Items en el editor
  private npcSprites: Map<string, {
    sprite: Phaser.GameObjects.Sprite;
    head: Phaser.GameObjects.Sprite | null;
    shield: Phaser.GameObjects.Sprite | null;
    weapon: Phaser.GameObjects.Sprite | null;
    label: Phaser.GameObjects.Text;
  }> = new Map();
  private itemSprites: Map<string, {
    sprite: Phaser.GameObjects.Rectangle;
    label: Phaser.GameObjects.Text;
  }> = new Map();
  
  // Visualización de luces en el editor
  private lightSprites: Map<string, {
    circle: Phaser.GameObjects.Arc;
    label: Phaser.GameObjects.Text;
  }> = new Map();
  private showLights: boolean = true;
  /** Marcadores de deco-objects (fogatas, etc.) en el editor. */
  private decoMarkers: Map<string, { marker: Phaser.GameObjects.Arc; label: Phaser.GameObjects.Text }> = new Map();

  // Visualización de translados en el editor (anillo + label + sprite opcional)
  private transladoSprites: Map<string, {
    ring: Phaser.GameObjects.Arc;
    label: Phaser.GameObjects.Text;
    sprite: Phaser.GameObjects.Image | null;
  }> = new Map();
  private showTranslados: boolean = true;

  // Visualización de áreas en el editor (rects + zonas de respawn + label)
  private areaSprites: Map<string, {
    rects: Phaser.GameObjects.Rectangle[];
    zoneRects: Phaser.GameObjects.Rectangle[];
    label: Phaser.GameObjects.Text | null;
  }> = new Map();
  private showAreas: boolean = true;
  // Modo dibujo de rectángulo de área (drag = rect, click = 1x1)
  private areaDrawMode: boolean = false;
  private areaDrawStart: { x: number; y: number } | null = null;
  private areaDrawCurrent: { x: number; y: number } | null = null;
  private areaDrawPreview: Phaser.GameObjects.Rectangle | null = null;

  // Modo de colocación de luces
  private lightPlacementMode: boolean = false;
  private pendingLightConfig: Omit<MapLightData, 'id' | 'x' | 'y'> | null = null;
  
  // Constantes de offset (igual que GameScene)
  private static readonly BODY_OFFSET_Y = -20;
  private static readonly BODY_FRAME_HEIGHT = 52;
  private static readonly HEAD_FRAME_HEIGHT = 30;
  private static readonly HEAD_OFFSET_FROM_BODY_BOTTOM = -30;
  private static readonly ENTITY_DEPTH_OFFSET = 0.2;
  
  // Preview de superficie para colocación
  private surfacePreview: Phaser.GameObjects.Image | null = null;
  private selectedSurfaceId: string | null = null;
  // Si está seteado, el editor está en modo "estampar casa entera" (todas las partes
  // en sus capas + colisión de una). Mutuamente excluyente con selectedSurfaceId.
  private selectedHouseId: string | null = null;
  private selectedLayerName: string = 'ground';
  private previewTileX: number = -1;
  private previewTileY: number = -1;
  
  // Historial para undo
  private actionHistory: Array<{
    type: 'place' | 'remove';
    x: number;
    y: number;
    layerName: string;
    previousSpriteId: string | null;
    newSpriteId: string | null;
  }> = [];
  private maxHistorySize: number = 50;

  // ===========================================================================
  // VIEWPORT CULLING DEL MAPA (espejo de GameScene, adaptado a edición)
  // El editor maneja mundos enormes (~390k tiles). En vez de crear un sprite por
  // tile al cargar (congela el hilo), indexamos los tiles por chunk y solo
  // instanciamos los visibles. El índice se keyea por identidad de tile
  // (`${layerName}#${x},${y}`) para soportar alta/baja O(1) en la edición.
  // ===========================================================================
  private mapTileEntries: Map<string, { tile: TileData; layerName: string }> = new Map();
  private mapTileChunks: Map<string, Set<string>> = new Map();
  private activeTileObjects: Map<string, Phaser.GameObjects.Image> = new Map();
  // Agua animada por bloque (128px) — espejo de GameScene.
  private waterSprites: Map<string, { sprite: Phaser.GameObjects.Image; type: string }> = new Map();
  private waterFrame = 0;
  private waterAnimTimer: Phaser.Time.TimerEvent | null = null;
  private activeMapChunks: Set<string> = new Set();
  private tileCreateQueue: string[] = [];
  private lastCullCamX: number = Number.NaN;
  private lastCullCamY: number = Number.NaN;
  private lastCullCamW: number = Number.NaN;
  private lastCullCamH: number = Number.NaN;
  private static readonly MAP_CHUNK_TILES = 16;
  private static readonly MAP_TILES_PER_FRAME = 250;
  private static readonly MAP_CULL_MARGIN_TILES = 8;

  constructor() {
    super({ key: 'MapEditorScene' });

    // Usar la propiedad estática que se actualiza ANTES de crear el juego
    this.configData = {
      mapData: MapEditorScene.currentMapData,
    };
  }

  public init(data: MapEditorConfig): void {
    // Usar la propiedad estática que se actualiza antes de crear el juego
    this.configData = {
      mapData: MapEditorScene.currentMapData,
    };
  }

  preload(): void {
    console.log('[MapEditorScene] preload() - Cargando assets para mapa:', this.configData.mapData.id);
    
    // Índice de assets (decoraciones) - siempre cargamos esto
    this.load.json('assetsIndex', '/assets/assets-index.json');
    
    // Cargar assets del mapa que está en configData
    const mapDataToLoad = this.configData.mapData;
    if (mapDataToLoad) {
      this.preloadMapAssets(mapDataToLoad);
    }

    // Frames de agua animada (reemplazan el agua estática).
    preloadWaterFrames(this);
    
    // Precargar sprites de personajes (body, head, shield, weapon) para NPCs
    // Obtener todos los IDs únicos usados en los templates de NPCs
    const allTemplates = getAllNPCTemplates();
    const bodyIds = new Set<number>([1]); // Default bodyId
    const headIds = new Set<number>();
    const shieldIds = new Set<number>();
    const weaponIds = new Set<number>();
    
    for (const template of allTemplates) {
      if (template.bodyId !== undefined) bodyIds.add(template.bodyId);
      if (template.headId !== undefined && template.headId > 0) headIds.add(template.headId);
      if (template.shieldId !== undefined && template.shieldId > 0) shieldIds.add(template.shieldId);
      if (template.weaponId !== undefined && template.weaponId > 0) weaponIds.add(template.weaponId);
    }
    
    preloadBodySprites(this, Array.from(bodyIds));
    preloadHeadSprites(this, Array.from(headIds));
    preloadShieldSprites(this, Array.from(shieldIds));
    preloadWeaponSprites(this, Array.from(weaponIds));
  }

  create(): void {
    console.log('[MapEditorScene] create() - Creando mapa:', this.configData.mapData.id);
    
    // Configurar cámara con límites basados en el mapa para que se detenga en los bordes
    // Calcular los bounds del mundo en píxeles
    const worldMinX = this.configData.mapData.bounds.minX * TILE_SIZE;
    const worldMaxX = (this.configData.mapData.bounds.maxX + 1) * TILE_SIZE;
    const worldMinY = this.configData.mapData.bounds.minY * TILE_SIZE;
    const worldMaxY = (this.configData.mapData.bounds.maxY + 1) * TILE_SIZE;
    
    // Establecer bounds de la cámara para limitar el movimiento dentro del mapa
    this.cameras.main.setBounds(worldMinX, worldMinY, worldMaxX - worldMinX, worldMaxY - worldMinY);
    
    // Zoom inicial a 1 (sin zoom) igual que el juego
    this.cameras.main.setZoom(1);
    this.cameras.main.centerOn(
      (this.configData.mapData.bounds.minX + this.configData.mapData.bounds.maxX) / 2 * TILE_SIZE,
      (this.configData.mapData.bounds.minY + this.configData.mapData.bounds.maxY) / 2 * TILE_SIZE
    );

    const rawAssetsIndex = this.cache.json.get('assetsIndex') as
      | { 
          decorations?: Record<string, { name: string; type: string }>;
          surfaces?: SurfaceIndex;
        }
      | undefined;
    if (rawAssetsIndex?.decorations) {
      this.decorationIndex = rawAssetsIndex.decorations;
      console.log('[MapEditorScene] Decoration index cargado:', Object.keys(this.decorationIndex).length, 'decoraciones');
    }
    
    // Cargar superficies desde atlas
    const hasSurfaces = rawAssetsIndex?.surfaces && Object.keys(rawAssetsIndex.surfaces).length > 0;
    if (hasSurfaces) {
      this.surfaceIndex = rawAssetsIndex.surfaces;
      console.log('[MapEditorScene] Surface index cargado:', Object.keys(this.surfaceIndex).length, 'superficies');
      
      // Precargar atlas de superficies
      preloadSurfaceAtlases(this, rawAssetsIndex as { surfaces?: SurfaceIndex });
      
      // Configurar texturas cuando se complete la carga, LUEGO cargar el mapa
      this.load.once('complete', () => {
        setupSurfaceTextures(this);
        console.log('[MapEditorScene] Superficies configuradas, ahora cargando mapa...');
        // Ahora sí cargar el mapa (las texturas ya están listas)
        this.loadAndCreateMap(this.configData.mapData);
      });
      
      this.load.start();
    } else {
      // Sin superficies, cargar el mapa directamente
      this.loadAndCreateMap(this.configData.mapData);
    }

    // Obtener todos los bodyIds únicos usados en los templates de NPCs
    const allTemplates = getAllNPCTemplates();
    const bodyIds = new Set<number>([1]); // Default bodyId
    for (const template of allTemplates) {
      if (template.bodyId !== undefined) bodyIds.add(template.bodyId);
    }
    const bodyIdsArray = Array.from(bodyIds);
    
    // Configurar spritesheets de body (DEBE llamarse antes de createBodyAnimations)
    setupBodySpritesheets(this, bodyIdsArray);
    
    // Crear animaciones de body (usar los mismos IDs que en preload)
    createBodyAnimations(this, bodyIdsArray, WALK_ANIM_FRAME_RATE);

    // Renderizar NPCs e Items iniciales
    this.renderNPCsAndItems();

    // Crear Graphics objects para grilla y tiles bloqueados (un solo objeto cada uno)
    this.gridGraphics = this.add.graphics();
    this.gridGraphics.setDepth(10000); // Muy alto para que se vea sobre todo
    this.blockedTileGraphics = this.add.graphics();
    this.blockedTileGraphics.setDepth(10001);
    this.borderLimitGraphics = this.add.graphics();
    this.borderLimitGraphics.setDepth(10002);
    
    // Rectángulo para resaltar el tile seleccionado (debe estar por encima de los bloqueados)
    this.selectedTileRect = this.add
      .rectangle(0, 0, TILE_SIZE, TILE_SIZE)
      .setStrokeStyle(2, 0xffff00)
      .setOrigin(0, 0)
      .setDepth(10003); // Por encima de todo (grilla, bloqueados, límites)

    // Las FPS ahora se muestran en el contenedor HTML, no en Phaser
    this.fpsText = null;

    this.cursors = this.input.keyboard!.createCursorKeys();
    
    // Tecla ESC para deseleccionar superficie
    this.input.keyboard!.on('keydown-ESC', () => {
      if (this.selectedSurfaceId || this.selectedHouseId) {
        this.setSelectedSurface(null);
        this.setSelectedHouse(null);
        if (MapEditorScene.onSurfaceDeselected) {
          MapEditorScene.onSurfaceDeselected();
        }
      }
    });
    
    // Ctrl+Z para undo
    this.input.keyboard!.on('keydown', (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key === 'z' && !event.shiftKey) {
        event.preventDefault();
        this.undo();
      }
    });

    // Click para seleccionar tile
    this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      // Click derecho: deseleccionar superficie si hay una seleccionada, sino iniciar arrastre
      if (pointer.rightButtonDown()) {
        if (this.selectedSurfaceId) {
          // Deseleccionar superficie
          this.setSelectedSurface(null);
          // Notificar a la UI (el callback se llama desde el panel HTML)
          if (MapEditorScene.onSurfaceDeselected) {
            MapEditorScene.onSurfaceDeselected();
          }
          return;
        }
        // Si no hay superficie seleccionada, iniciar arrastre
        this.isDragging = true;
        this.dragStartX = pointer.x;
        this.dragStartY = pointer.y;
        this.dragStartScrollX = this.cameras.main.scrollX;
        this.dragStartScrollY = this.cameras.main.scrollY;
        return;
      }
      
      // Botón medio: siempre arrastrar
      if (pointer.middleButtonDown()) {
        this.isDragging = true;
        this.dragStartX = pointer.x;
        this.dragStartY = pointer.y;
        this.dragStartScrollX = this.cameras.main.scrollX;
        this.dragStartScrollY = this.cameras.main.scrollY;
        return;
      }

      // Click izquierdo normal para seleccionar tile
      this.isPointerDown = true;
      const worldPoint = this.cameras.main.getWorldPoint(pointer.x, pointer.y);
      const tileX = Math.floor(worldPoint.x / TILE_SIZE);
      const tileY = Math.floor(worldPoint.y / TILE_SIZE);

      if (!this.currentMap) return;

      // Clamp a bounds del mapa solo para la selección
      const { minX, maxX, minY, maxY } = this.currentMap.bounds;
      const clampedX = Phaser.Math.Clamp(tileX, minX, maxX);
      const clampedY = Phaser.Math.Clamp(tileY, minY, maxY);

      // Modo dibujo de área: registrar inicio del rectángulo (drag = rect, click = 1x1).
      if (this.areaDrawMode) {
        this.areaDrawStart = { x: clampedX, y: clampedY };
        this.areaDrawCurrent = { x: clampedX, y: clampedY };
        this.updateAreaDrawPreview();
        return;
      }

      this.selectedTileX = clampedX;
      this.selectedTileY = clampedY;
      this.updateSelectedTileRect();

      // Si estamos en modo de colocación de luz, colocar una luz
      if (this.lightPlacementMode && this.pendingLightConfig) {
        this.placeLightAtSelectedTile();
      }
      // Si estamos en modo "casa entera", estampar la casa completa
      else if (this.selectedHouseId) {
        this.previewTileX = clampedX;
        this.previewTileY = clampedY;
        this.placeSelectedHouse();
      }
      // Si hay una superficie seleccionada, colocarla
      else if (this.selectedSurfaceId) {
        this.previewTileX = clampedX;
        this.previewTileY = clampedY;
        this.placeSelectedSurface();
      }

      // Mostrar tooltip
      this.showTooltip(pointer.x, pointer.y, clampedX, clampedY);

      if (MapEditorScene.onTileSelected) {
        MapEditorScene.onTileSelected(this.selectedTileX, this.selectedTileY);
      }
    });

    this.input.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      // Si estamos arrastrando, mover la cámara (los bounds de Phaser limitarán automáticamente)
      if (this.isDragging) {
        const deltaX = this.dragStartX - pointer.x;
        const deltaY = this.dragStartY - pointer.y;
        const cam = this.cameras.main;
        // Phaser automáticamente limitará el scroll dentro de los bounds establecidos
        cam.setScroll(this.dragStartScrollX + deltaX / cam.zoom, this.dragStartScrollY + deltaY / cam.zoom);
        return;
      }

      // Modo dibujo de área: actualizar el rectángulo en curso.
      if (this.areaDrawMode && this.areaDrawStart && this.currentMap) {
        const worldPoint = this.cameras.main.getWorldPoint(pointer.x, pointer.y);
        const { minX, maxX, minY, maxY } = this.currentMap.bounds;
        this.areaDrawCurrent = {
          x: Phaser.Math.Clamp(Math.floor(worldPoint.x / TILE_SIZE), minX, maxX),
          y: Phaser.Math.Clamp(Math.floor(worldPoint.y / TILE_SIZE), minY, maxY),
        };
        this.updateAreaDrawPreview();
        return;
      }

      // Actualizar preview de superficie/casa (siempre que haya una seleccionada)
      if ((this.selectedSurfaceId || this.selectedHouseId) && this.currentMap) {
        const worldPoint = this.cameras.main.getWorldPoint(pointer.x, pointer.y);
        const tileX = Math.floor(worldPoint.x / TILE_SIZE);
        const tileY = Math.floor(worldPoint.y / TILE_SIZE);

        const { minX, maxX, minY, maxY } = this.currentMap.bounds;
        const clampedX = Phaser.Math.Clamp(tileX, minX, maxX);
        const clampedY = Phaser.Math.Clamp(tileY, minY, maxY);

        // Solo actualizar si cambió la posición
        if (this.previewTileX !== clampedX || this.previewTileY !== clampedY) {
          this.previewTileX = clampedX;
          this.previewTileY = clampedY;
          if (this.selectedHouseId) {
            this.updateHousePreview();
          } else {
            this.updateSurfacePreview();
          }
        }
      }

      // Si estamos seleccionando tile (click izquierdo)
      if (this.isPointerDown) {
        const worldPoint = this.cameras.main.getWorldPoint(pointer.x, pointer.y);
        const tileX = Math.floor(worldPoint.x / TILE_SIZE);
        const tileY = Math.floor(worldPoint.y / TILE_SIZE);

        if (!this.currentMap) return;

        const { minX, maxX, minY, maxY } = this.currentMap.bounds;
        const clampedX = Phaser.Math.Clamp(tileX, minX, maxX);
        const clampedY = Phaser.Math.Clamp(tileY, minY, maxY);

        // Actualizar tile seleccionado si cambió
        if (this.selectedTileX !== clampedX || this.selectedTileY !== clampedY) {
          this.selectedTileX = clampedX;
          this.selectedTileY = clampedY;
          this.updateSelectedTileRect();

          // Notificar cambio de tile
          if (MapEditorScene.onTileSelected) {
            MapEditorScene.onTileSelected(this.selectedTileX, this.selectedTileY);
          }
        }

        // Actualizar tooltip
        this.updateTooltip(pointer.x, pointer.y, clampedX, clampedY);
      }
    });

    this.input.on('pointerup', () => {
      // Finalizar dibujo de rectángulo de área.
      if (this.areaDrawMode && this.areaDrawStart && this.areaDrawCurrent) {
        const sx = Math.min(this.areaDrawStart.x, this.areaDrawCurrent.x);
        const sy = Math.min(this.areaDrawStart.y, this.areaDrawCurrent.y);
        const ex = Math.max(this.areaDrawStart.x, this.areaDrawCurrent.x);
        const ey = Math.max(this.areaDrawStart.y, this.areaDrawCurrent.y);
        const rect: MapRect = { x: sx, y: sy, w: ex - sx + 1, h: ey - sy + 1 };
        this.areaDrawStart = null;
        this.areaDrawCurrent = null;
        if (this.areaDrawPreview) { this.areaDrawPreview.destroy(); this.areaDrawPreview = null; }
        if (MapEditorScene.onAreaRectDrawn) MapEditorScene.onAreaRectDrawn(rect);
      }
      this.isPointerDown = false;
      this.isDragging = false;
      this.hideTooltip();
    });

    // Zoom con la rueda del mouse (centrado en el centro de la pantalla)
    this.input.on('wheel', (pointer: Phaser.Input.Pointer, gameObjects: Phaser.GameObjects.GameObject[], deltaX: number, deltaY: number, deltaZ: number) => {
      const cam = this.cameras.main;
      const currentZoom = cam.zoom;
      const zoomSpeed = 0.1;
      const minZoom = 0.2;
      const maxZoom = 3.0;
      
      // deltaY negativo = scroll hacia arriba (zoom in), positivo = scroll hacia abajo (zoom out)
      let newZoom = currentZoom;
      if (deltaY < 0) {
        newZoom = Math.min(maxZoom, currentZoom + zoomSpeed);
      } else if (deltaY > 0) {
        newZoom = Math.max(minZoom, currentZoom - zoomSpeed);
      }
      
      if (newZoom !== currentZoom) {
        // Zoom centrado en el centro de la pantalla
        const { width, height } = this.scale;
        const centerX = width / 2;
        const centerY = height / 2;
        
        // Obtener el punto del mundo en el centro de la pantalla ANTES del zoom
        // Usar getWorldPoint que maneja correctamente el zoom actual
        const worldPointBefore = cam.getWorldPoint(centerX, centerY);
        
        // Aplicar el nuevo zoom
        cam.setZoom(newZoom);
        
        // Centrar la cámara en el mismo punto del mundo que estaba en el centro antes del zoom
        // Esto asegura que el zoom se haga centrado en ese punto
        cam.centerOn(worldPointBefore.x, worldPointBefore.y);
        
        // Actualizar grilla y tiles bloqueados después del zoom
        this.updateGridWindow();
        this.updateBlockedTilesWindow();
        
        // Las FPS ya están fijas con setScrollFactor(0), no necesitan reposicionarse
        
        // Notificar cambio de zoom para actualizar el minimapa
        // Usar un pequeño delay para asegurar que el zoom se haya aplicado
        setTimeout(() => {
          // El minimapa se actualiza periódicamente, así que se actualizará automáticamente
        }, 10);
      }
    });
  }

  update(time: number, delta: number): void {
    // Actualizar FPS en el elemento HTML
    const fpsElement = document.getElementById('map-editor-fps');
    if (fpsElement) {
      const fps = Math.round(1000 / delta);
      fpsElement.textContent = `${fps}`;
    }

    if (!this.cursors) return;

    const cam = this.cameras.main;
    const speed = TILE_SIZE * 0.5; // Velocidad más rápida pero simple
    let moved = false;

    // Phaser automáticamente limitará el scroll dentro de los bounds establecidos
    if (this.cursors.left?.isDown) {
      cam.scrollX -= speed;
      moved = true;
    }
    if (this.cursors.right?.isDown) {
      cam.scrollX += speed;
      moved = true;
    }
    if (this.cursors.up?.isDown) {
      cam.scrollY -= speed;
      moved = true;
    }
    if (this.cursors.down?.isDown) {
      cam.scrollY += speed;
      moved = true;
    }

    // Actualizar grilla y tiles bloqueados si la cámara se movió o están activos
    if (moved || this.showGrid || this.showBlockedTiles || this.showMapBorders) {
      this.updateGridWindow();
      this.updateBlockedTilesWindow();
    }

    if (moved) {
      this.updateSelectedTileRect();
    }

    // Streaming de chunks del mapa según el viewport + amortizado por frame.
    this.updateMapCulling(false);
    this.processTileCreateQueue();
  }

  private updateSelectedTileRect(): void {
    if (!this.selectedTileRect) return;
    this.selectedTileRect.setPosition(this.selectedTileX * TILE_SIZE, this.selectedTileY * TILE_SIZE);
  }

  private showTooltip(screenX: number, screenY: number, tileX: number, tileY: number): void {
    // Posición del tooltip: abajo a la derecha del cursor
    // Usar coordenadas de pantalla directamente (no afectadas por zoom)
    const tooltipX = screenX + 10;
    const tooltipY = screenY + 10;

    // Crear fondo negro
    const bg = this.add.rectangle(0, 0, 80, 24, 0x000000, 0.8);
    bg.setOrigin(0, 0);
    bg.setScrollFactor(0); // Fijo en pantalla

    // Crear texto con coordenadas
    const text = this.add.text(4, 4, `(${tileX}, ${tileY})`, {
      fontSize: '12px',
      color: '#ffffff',
      fontFamily: GAME_FONT_FAMILY,
      resolution: 2, // Resolución alta para textos nítidos
    });
    text.setOrigin(0, 0);
    text.setScrollFactor(0); // Fijo en pantalla

    // Ajustar ancho del fondo al texto
    bg.setSize(text.width + 8, 24);

    // Crear container para el tooltip
    this.tooltip = this.add.container(tooltipX, tooltipY);
    this.tooltip.add([bg, text]);
    this.tooltip.setDepth(10001);
    this.tooltip.setScrollFactor(0); // Fijo en pantalla - no se mueve con zoom
  }

  private updateTooltip(screenX: number, screenY: number, tileX: number, tileY: number): void {
    if (!this.tooltip) return;
    const tooltipX = screenX + 10;
    const tooltipY = screenY + 10;
    this.tooltip.setPosition(tooltipX, tooltipY);
    
    // Actualizar texto
    const text = this.tooltip.list[1] as Phaser.GameObjects.Text;
    if (text) {
      text.setText(`(${tileX}, ${tileY})`);
      // Ajustar fondo
      const bg = this.tooltip.list[0] as Phaser.GameObjects.Rectangle;
      if (bg) {
        bg.setSize(text.width + 8, 24);
      }
    }
  }

  private hideTooltip(): void {
    if (this.tooltip) {
      this.tooltip.destroy();
      this.tooltip = null;
    }
  }

  /**
   * Renderiza los NPCs e Items en el mapa del editor, igual que en el juego.
   */
  private renderNPCsAndItems(): void {
    // Limpiar sprites anteriores
    this.npcSprites.forEach((obj) => {
      obj.sprite.destroy();
      obj.head?.destroy();
      obj.shield?.destroy();
      obj.weapon?.destroy();
      obj.label.destroy();
    });
    this.itemSprites.forEach((obj) => {
      obj.sprite.destroy();
      obj.label.destroy();
    });
    this.npcSprites.clear();
    this.itemSprites.clear();

    // Renderizar NPCs (igual que en GameScene)
    for (const npc of MapEditorScene.npcPlacements) {
      const worldX = npc.x * TILE_SIZE + TILE_SIZE / 2;
      const worldY = npc.y * TILE_SIZE + TILE_SIZE / 2;

      // Obtener template del NPC para usar sus propiedades visuales
      const template = getNPCTemplate(npc.templateId);
      
      // Usar valores del template con fallback a defaults
      const bodyId = template?.bodyId ?? 1;
      const headId = template?.headId ?? 0;
      const shieldId = template?.shieldId ?? 0;
      const weaponId = template?.weaponId ?? 0;
      const npcName = template?.name ?? npc.templateId;
      const direction = Direction.DOWN;

      const bodyY = worldY + MapEditorScene.BODY_OFFSET_Y;
      const bodyBottomY = bodyY + MapEditorScene.BODY_FRAME_HEIGHT / 2;
      const headCenterY =
        bodyBottomY +
        MapEditorScene.HEAD_OFFSET_FROM_BODY_BOTTOM -
        MapEditorScene.HEAD_FRAME_HEIGHT / 2;

      // SOLO el body usa el offset para centrar el arte
      const bodyCenterOffsetX = getBodyCenterOffset(bodyId);
      const bodyX = worldX + bodyCenterOffsetX;

      // Crear sprites del NPC - solo el body usa el offset
      const sprite = this.add.sprite(bodyX, bodyBottomY, getBodySpriteKey(bodyId));
      sprite.setOrigin(0.5, 1);
      sprite.setFrame(getBodyIdleFrame(direction, bodyId));

      // Todo lo demás se centra en el tile (sin offset del body)
      const headSprite =
        headId > 0
          ? this.add.sprite(worldX, headCenterY, getHeadSpriteKey(headId)).setOrigin(0.5, 0.5)
          : null;
      if (headSprite) {
        headSprite.setFrame(getHeadFrameForDirection(direction));
      }

      const shieldSprite =
        shieldId > 0
          ? this.add.sprite(worldX - 4, bodyBottomY, getShieldSpriteKey(shieldId)).setOrigin(0.5, 1)
          : null;
      if (shieldSprite) {
        shieldSprite.setFrame(getShieldIdleFrame(direction));
      }

      const weaponSprite =
        weaponId > 0
          ? this.add.sprite(worldX - 1, bodyBottomY, getWeaponSpriteKey(weaponId)).setOrigin(0.5, 1)
          : null;
      if (weaponSprite) {
        weaponSprite.setFrame(getWeaponIdleFrame(direction));
      }

      // Mostrar el nombre del NPC en lugar del templateId
      const label = this.add
        .text(worldX, bodyBottomY + 4, npcName, {
          fontSize: '10px',
          color: '#ffffaa',
        })
        .setOrigin(0.5, 0)
        .setResolution(1); // Resolución nativa para mejor nitidez

      // Aplicar profundidades (igual que GameScene)
      const baseDepth = this.getEntityBaseDepthFromTileY(npc.y);
      const headDelta = 0.01;
      const frontDelta = 0.02;
      const backDelta = -0.02;
      const labelDelta = 0.03;

      sprite.setDepth(baseDepth + backDelta);
      if (headSprite) headSprite.setDepth(baseDepth + headDelta);
      if (shieldSprite) shieldSprite.setDepth(baseDepth + frontDelta);
      if (weaponSprite) weaponSprite.setDepth(baseDepth + frontDelta);
      label.setDepth(baseDepth + labelDelta);

      this.npcSprites.set(`${npc.x},${npc.y}`, {
        sprite,
        head: headSprite,
        shield: shieldSprite,
        weapon: weaponSprite,
        label,
      });
    }

    // Renderizar Items (igual que en GameScene)
    for (const item of MapEditorScene.itemPlacements) {
      const worldX = item.x * TILE_SIZE + TILE_SIZE / 2;
      const worldY = item.y * TILE_SIZE + TILE_SIZE / 2;

      // Profundidad: siempre por debajo de las entidades
      const baseDepthForEntities = this.getEntityBaseDepthFromTileY(item.y);
      const itemRectDepth = baseDepthForEntities - 0.05;
      const itemLabelDepth = baseDepthForEntities - 0.04;

      const rect = this.add
        .rectangle(worldX, worldY, 10, 10, 0xffff00, 0.9)
        .setOrigin(0.5)
        .setDepth(itemRectDepth);

      const label = this.add
        .text(worldX, worldY - 16, item.itemId, {
          fontSize: '8px',
          color: '#ffffaa',
          backgroundColor: 'rgba(0,0,0,0.5)',
        })
        .setOrigin(0.5)
        .setDepth(itemLabelDepth)
        .setResolution(1); // Resolución nativa para mejor nitidez

      this.itemSprites.set(`${item.x},${item.y}`, {
        sprite: rect,
        label,
      });
    }
  }

  /**
   * Calcula la profundidad base para entidades (igual que GameScene).
   */
  private getEntityBaseDepthFromTileY(tileY: number): number {
    return this.getDepthFromTileY(tileY) + MapEditorScene.ENTITY_DEPTH_OFFSET;
  }

  /**
   * Verifica si un tile está en el borde del mapa (30 tiles de cada lado).
   * Cálculo matemático O(1), no usa Sets.
   */
  private isBorderTile(x: number, y: number): boolean {
    if (!this.currentMap) return false;
    const { minX, maxX, minY, maxY } = this.currentMap.bounds;
    const border = MAP_BORDER_SIZE;
    
    return (
      x < minX + border ||
      x > maxX - border ||
      y < minY + border ||
      y > maxY - border
    );
  }

  /**
   * Actualiza la visualización de tiles bloqueados solo en la ventana visible (eficiente).
   */
  private updateBlockedTilesWindow(): void {
    // Limpiar gráficos siempre
    if (this.blockedTileGraphics) this.blockedTileGraphics.clear();
    if (this.borderLimitGraphics) this.borderLimitGraphics.clear();

    if (!this.blockedTiles || !this.currentMap) return;

    const cam = this.cameras.main;
    const view = cam.worldView;
    const margin = 5;

    const startGX = Math.max(0, Math.floor(view.x / TILE_SIZE) - margin);
    const startGY = Math.max(0, Math.floor(view.y / TILE_SIZE) - margin);
    const endGX = Math.min(WORLD_TILES - 1, Math.ceil(view.right / TILE_SIZE) + margin);
    const endGY = Math.min(WORLD_TILES - 1, Math.ceil(view.bottom / TILE_SIZE) + margin);

    // Renderizar border_limit primero (si está activo)
    // Usa cálculo matemático O(1) en vez de buscar en Set
    if (this.showMapBorders && this.borderLimitGraphics) {
      for (let gx = startGX; gx <= endGX; gx += 1) {
        for (let gy = startGY; gy <= endGY; gy += 1) {
          if (this.isBorderTile(gx, gy)) {
            const px = gx * TILE_SIZE;
            const py = gy * TILE_SIZE;
            this.borderLimitGraphics.fillStyle(0xcc0000, 0.1); // Muy tenue
            this.borderLimitGraphics.fillRect(px, py, TILE_SIZE, TILE_SIZE);
          }
        }
      }
    }

    // Renderizar tiles bloqueados normales (si está activo)
    if (this.showBlockedTiles && this.blockedTiles && this.blockedTileGraphics) {
      for (let gx = startGX; gx <= endGX; gx += 1) {
        for (let gy = startGY; gy <= endGY; gy += 1) {
          const tileKey = `${gx},${gy}`;
          // Solo renderizar si está bloqueado Y no es borde
          if (this.blockedTiles.has(tileKey) && !this.isBorderTile(gx, gy)) {
            const px = gx * TILE_SIZE;
            const py = gy * TILE_SIZE;
            this.blockedTileGraphics.lineStyle(2, 0xff0000, 1);
            this.blockedTileGraphics.strokeRect(px, py, TILE_SIZE, TILE_SIZE);

            this.blockedTileGraphics.lineStyle(2, 0xff0000, 0.8);
            this.blockedTileGraphics.moveTo(px, py);
            this.blockedTileGraphics.lineTo(px + TILE_SIZE, py + TILE_SIZE);
            this.blockedTileGraphics.strokePath();
            this.blockedTileGraphics.moveTo(px + TILE_SIZE, py);
            this.blockedTileGraphics.lineTo(px, py + TILE_SIZE);
            this.blockedTileGraphics.strokePath();
          }
        }
      }
    }
  }

  /**
   * Actualiza la grilla visible (igual que GameScene).
   */
  private updateGridWindow(): void {
    if (!this.showGrid || !this.gridGraphics) {
      if (this.gridGraphics) this.gridGraphics.clear();
      return;
    }

    const cam = this.cameras.main;
    const view = cam.worldView;
    const margin = 5;

    const startGX = Math.max(0, Math.floor(view.x / TILE_SIZE) - margin);
    const startGY = Math.max(0, Math.floor(view.y / TILE_SIZE) - margin);
    const endGX = Math.min(WORLD_TILES - 1, Math.ceil(view.right / TILE_SIZE) + margin);
    const endGY = Math.min(WORLD_TILES - 1, Math.ceil(view.bottom / TILE_SIZE) + margin);

    // Solo recalcular si cambió la ventana visible
    if (this.lastGridTileStartX === startGX && this.lastGridTileStartY === startGY) {
      return;
    }

    this.lastGridTileStartX = startGX;
    this.lastGridTileStartY = startGY;

    this.gridGraphics.clear();
    this.gridGraphics.lineStyle(1, 0x333333, 1);

    // Líneas verticales
    for (let gx = startGX; gx <= endGX + 1; gx += 1) {
      const x = gx * TILE_SIZE;
      this.gridGraphics.moveTo(x, startGY * TILE_SIZE);
      this.gridGraphics.lineTo(x, (endGY + 1) * TILE_SIZE);
    }

    // Líneas horizontales
    for (let gy = startGY; gy <= endGY + 1; gy += 1) {
      const y = gy * TILE_SIZE;
      this.gridGraphics.moveTo(startGX * TILE_SIZE, y);
      this.gridGraphics.lineTo((endGX + 1) * TILE_SIZE, y);
    }

    this.gridGraphics.strokePath();

    // Cruz central del mundo: columna + fila del medio (tile WORLD_TILES/2).
    // Mundo impar → centro único; ayuda a ubicar la mitad del mapa al editar.
    const mid = Math.floor(WORLD_TILES / 2);
    if (mid >= startGX && mid <= endGX) {
      this.gridGraphics.fillStyle(0xffd700, 0.15);
      this.gridGraphics.fillRect(mid * TILE_SIZE, startGY * TILE_SIZE, TILE_SIZE, (endGY - startGY + 1) * TILE_SIZE);
    }
    if (mid >= startGY && mid <= endGY) {
      this.gridGraphics.fillStyle(0xffd700, 0.15);
      this.gridGraphics.fillRect(startGX * TILE_SIZE, mid * TILE_SIZE, (endGX - startGX + 1) * TILE_SIZE, TILE_SIZE);
    }
    if (mid >= startGX && mid <= endGX && mid >= startGY && mid <= endGY) {
      this.gridGraphics.lineStyle(3, 0xffd700, 0.9);
      this.gridGraphics.strokeRect(mid * TILE_SIZE, mid * TILE_SIZE, TILE_SIZE, TILE_SIZE);
    }
  }

  /**
   * Métodos públicos para toggle de visualización.
   */
  public setShowGrid(show: boolean): void {
    this.showGrid = show;
    this.updateGridWindow();
  }

  public setShowBlockedTiles(show: boolean): void {
    this.showBlockedTiles = show;
    this.updateBlockedTilesWindow();
  }

  public setShowMapBorders(show: boolean): void {
    this.showMapBorders = show;
    this.updateBlockedTilesWindow();
  }

  /**
   * Método público para cambiar el estado de bloqueo de un tile.
   * Busca el tile en cualquier capa y cambia su propiedad blocked.
   */
  public toggleTileBlocked(x: number, y: number): boolean {
    if (!this.currentMap || !this.blockedTiles) return false;

    const tileKey = `${x},${y}`;
    const isBlocked = this.blockedTiles.has(tileKey);

    if (isBlocked) {
      // Desbloquear: quitar blocked de cualquier tile en esa posición
      this.blockedTiles.delete(tileKey);
      if (this.borderLimitTiles) {
        this.borderLimitTiles.delete(tileKey);
      }
      for (const layer of this.currentMap.layers) {
        const tile = layer.tiles.find(t => t.x === x && t.y === y);
        if (tile) {
          tile.blocked = false;
        }
      }
    } else {
      // Bloquear: buscar tile existente o crear uno nuevo en ground_deco_over
      this.blockedTiles.add(tileKey);
      
      let tileFound = false;
      for (const layer of this.currentMap.layers) {
        const tile = layer.tiles.find(t => t.x === x && t.y === y);
        if (tile) {
          tile.blocked = true;
          tileFound = true;
          break; // Solo marcar el primero que encontremos
        }
      }
      
      // Si no hay tile gráfico, crear uno invisible en una capa de colisión
      if (!tileFound) {
        let collisionLayer = this.currentMap.layers.find(l => l.name === '_collision');
        if (!collisionLayer) {
          collisionLayer = {
            name: '_collision',
            z: -1,
            tiles: [],
          };
          this.currentMap.layers.push(collisionLayer);
        }
        collisionLayer.tiles.push({
          x,
          y,
          sprite: 'blocked',
          blocked: true,
        });
      }
    }

    // Actualizar visualización
    this.updateBlockedTilesWindow();

    return !isBlocked; // Retorna el nuevo estado (true = bloqueado, false = desbloqueado)
  }

  /**
   * Método público para actualizar los NPCs e Items desde el editor HTML.
   */
  public updateNPCsAndItems(
    npcs: Array<{ templateId: string; x: number; y: number }>,
    items: Array<{ itemId: string; x: number; y: number; quantity: number }>
  ): void {
    MapEditorScene.npcPlacements = npcs;
    MapEditorScene.itemPlacements = items;
    this.renderNPCsAndItems();
  }

  /**
   * Precarga los assets necesarios para un mapa dado.
   * Copiado de GameScene.preloadMapAssets para reutilizar la lógica de tiles.
   */
  private preloadMapAssets(mapData: MapData): void {
    const seen = new Set<string>();

    const loadKey = (key: string | undefined): void => {
      if (!key || seen.has(key)) return;
      seen.add(key);
      if (isDecorationKey(key)) {
        this.load.image(key, getDecorationAssetUrl(key));
      } else if (key.startsWith('item_icon_')) {
        const itemFile = key.replace('item_icon_', '');
        this.load.image(key, `/assets/item/${itemFile}.png`);
      }
    };

    for (const layer of mapData.layers) {
      if (layer.name.startsWith('_')) continue;
      for (const tile of layer.tiles) {
        loadKey(tile.sprite);
      }
    }

    // Sprites de translados: cargarlos también para que se vean en el editor.
    if (mapData.translados) {
      for (const t of mapData.translados) {
        loadKey(t.sprite);
      }
    }
  }

  /**
   * Carga un mapa lógico (MapData) y crea sus sprites en pantalla.
   * Basado en GameScene.loadAndCreateMap, pero sin lógica de entidades ni HUD.
   */
  private loadAndCreateMap(mapData: MapData): void {
    this.currentMap = mapData;

    // Limpiar gráficos y tiles anteriores (solo limpiar, no destruir los Graphics)
    if (this.blockedTileGraphics) this.blockedTileGraphics.clear();
    if (this.borderLimitGraphics) this.borderLimitGraphics.clear();
    if (this.gridGraphics) this.gridGraphics.clear();
    this.clearMapTiles();

    // Indexar tiles bloqueados (excluyendo capas internas como _border)
    this.blockedTiles = new Set<string>();
    this.borderLimitTiles = new Set<string>();
    for (const layer of mapData.layers) {
      // Saltar capa de bordes - se maneja por separado
      if (layer.name === '_border') continue;

      for (const tile of layer.tiles) {
        if (tile.blocked) {
          const tileKey = `${tile.x},${tile.y}`;
          this.blockedTiles.add(tileKey);
        }
      }
    }

    // Los borderLimitTiles se calculan dinámicamente (no se indexan todos)
    // Solo se usan para verificar si un tile específico es borde

    // Actualizar visualización de tiles bloqueados (solo ventana visible)
    this.updateBlockedTilesWindow();

    // Construir el índice de tiles por chunk y crear SOLO los chunks visibles.
    // Un mundo generado tiene ~390k tiles: crearlos todos congelaría el editor.
    this.buildMapTileIndex(mapData);
    this.updateMapCulling(true);

    // Pedir al minimapa que re-hornee su terreno con el mapa nuevo.
    MapEditorScene.minimapDirty = true;

    // Inicializar luces del mapa
    this.initializeLights();

    // Inicializar translados del mapa
    this.initializeTranslados();

    // Inicializar áreas del mapa
    this.initializeAreas();

    // Inicializar cardúmenes de pesca del mapa
    this.initializeFishingSpots();

    // Agua animada: arrancar el timer global de frames (idempotente).
    this.ensureWaterAnimTimer();
  }

  // ===========================================================================
  // VIEWPORT CULLING DEL MAPA (implementación)
  // ===========================================================================

  /** entryKey único por tile: identidad capa + coordenada. */
  private tileEntryKey(layerName: string, x: number, y: number): string {
    return `${layerName}#${x},${y}`;
  }

  /** chunkKey de un tile (x,y) en coords de tile. */
  private chunkKeyForTile(tx: number, ty: number): string {
    const cs = MapEditorScene.MAP_CHUNK_TILES;
    return `${Math.floor(tx / cs)},${Math.floor(ty / cs)}`;
  }

  /**
   * Construye el índice de tiles (por identidad) y los buckets por chunk a partir
   * del MapData. No crea GameObjects.
   */
  private buildMapTileIndex(mapData: MapData): void {
    this.mapTileEntries = new Map();
    this.mapTileChunks = new Map();
    this.activeTileObjects = new Map();
    this.activeMapChunks = new Set();
    this.tileCreateQueue = [];
    this.lastCullCamX = Number.NaN;
    this.lastCullCamY = Number.NaN;

    for (const layer of mapData.layers) {
      if (layer.name.startsWith('_')) continue;
      for (const tile of layer.tiles) {
        this.indexTileEntry(tile, layer.name);
      }
    }
  }

  /** Da de alta (o reemplaza) una entry en el índice + su bucket de chunk. */
  private indexTileEntry(tile: TileData, layerName: string): string {
    const key = this.tileEntryKey(layerName, tile.x, tile.y);
    this.mapTileEntries.set(key, { tile, layerName });
    const chunkKey = this.chunkKeyForTile(tile.x, tile.y);
    let bucket = this.mapTileChunks.get(chunkKey);
    if (!bucket) {
      bucket = new Set();
      this.mapTileChunks.set(chunkKey, bucket);
    }
    bucket.add(key);
    return key;
  }

  /** Quita una entry del índice + su bucket (no toca el GameObject). */
  private unindexTileEntry(layerName: string, x: number, y: number): void {
    const key = this.tileEntryKey(layerName, x, y);
    this.mapTileEntries.delete(key);
    const bucket = this.mapTileChunks.get(this.chunkKeyForTile(x, y));
    bucket?.delete(key);
  }

  /**
   * Crea el GameObject de una entrada de tile. Replica la lógica de render
   * original: textura de surface, origin centrado, offset de árboles, depth.
   */
  private createMapTileObject(key: string): void {
    if (this.activeTileObjects.has(key)) return;
    const entry = this.mapTileEntries.get(key);
    if (!entry) return;
    const { tile, layerName } = entry;

    const worldX = tile.x * TILE_SIZE + TILE_SIZE / 2;
    const isGroundLike =
      layerName === 'ground' ||
      layerName === 'ground_deco_under' ||
      layerName === 'layer1_ground' ||
      layerName === 'layer2_floor_objects';
    const worldY = isGroundLike
      ? tile.y * TILE_SIZE + TILE_SIZE / 2
      : tile.y * TILE_SIZE + TILE_SIZE;

    // Agua animada por BLOQUE (128px): un sprite en el tile origen del bloque.
    const waterType = getWaterTypeForSprite(tile.sprite);
    if (waterType) {
      if (tile.x % 4 !== 0 || tile.y % 4 !== 0) return; // cubierto por el sprite del bloque
      const blockKey = getWaterFrameTextureKey(waterType, this.waterFrame % getWaterFrameCount(waterType));
      if (!this.textures.exists(blockKey)) return;
      const blockX = tile.x * TILE_SIZE + (4 * TILE_SIZE) / 2;
      const blockY = tile.y * TILE_SIZE + (4 * TILE_SIZE) / 2;
      const waterSprite = this.add.image(blockX, blockY, blockKey);
      waterSprite.setOrigin(0.5, 0.5);
      waterSprite.setDepth(this.getDepthForMapLayer(layerName, tile.y, tile.zOverride));
      this.activeTileObjects.set(key, waterSprite);
      this.waterSprites.set(key, { sprite: waterSprite, type: waterType });
      return;
    }

    // Resolver textura (surfaces usan key generada por SurfaceSpriteManager).
    let textureKey = tile.sprite;
    const surfaceTextureKey = getSurfaceTextureKey(tile.sprite);
    if (this.textures.exists(surfaceTextureKey)) {
      textureKey = surfaceTextureKey;
    }
    if (!this.textures.exists(textureKey)) {
      // Sin textura: no crear (no spamear warning por frame).
      return;
    }

    const sprite = this.add.image(worldX, worldY, textureKey);
    sprite.setOrigin(0.5, isGroundLike ? 0.5 : 1);

    sprite.setDepth(this.getDepthForMapLayer(layerName, tile.y, tile.zOverride));
    this.activeTileObjects.set(key, sprite);
  }

  /** Destruye el GameObject de una entrada de tile (si está instanciado). */
  private destroyMapTileObject(key: string): void {
    const obj = this.activeTileObjects.get(key);
    if (!obj) return;
    obj.destroy();
    this.activeTileObjects.delete(key);
    this.waterSprites.delete(key);
  }

  /**
   * Recalcula qué chunks deben estar instanciados según el viewport de la cámara
   * (+ margen) y crea/destruye los GameObjects necesarios.
   * @param force ignora el throttle por movimiento de cámara y crea los chunks
   *   visibles de una (carga inicial).
   */
  private updateMapCulling(force = false): void {
    if (this.mapTileEntries.size === 0) return;
    const cam = this.cameras.main;
    if (!cam) return;

    const view = cam.worldView;
    if (
      !force &&
      Math.abs(view.x - this.lastCullCamX) < TILE_SIZE &&
      Math.abs(view.y - this.lastCullCamY) < TILE_SIZE &&
      view.width === this.lastCullCamW &&
      view.height === this.lastCullCamH
    ) {
      return;
    }
    this.lastCullCamX = view.x;
    this.lastCullCamY = view.y;
    this.lastCullCamW = view.width;
    this.lastCullCamH = view.height;

    const margin = MapEditorScene.MAP_CULL_MARGIN_TILES * TILE_SIZE;
    const minTileX = Math.floor((view.x - margin) / TILE_SIZE);
    const minTileY = Math.floor((view.y - margin) / TILE_SIZE);
    const maxTileX = Math.ceil((view.right + margin) / TILE_SIZE);
    const maxTileY = Math.ceil((view.bottom + margin) / TILE_SIZE);

    const cs = MapEditorScene.MAP_CHUNK_TILES;
    const minCx = Math.floor(minTileX / cs);
    const minCy = Math.floor(minTileY / cs);
    const maxCx = Math.floor(maxTileX / cs);
    const maxCy = Math.floor(maxTileY / cs);

    const needed = new Set<string>();
    for (let cy = minCy; cy <= maxCy; cy++) {
      for (let cx = minCx; cx <= maxCx; cx++) {
        needed.add(`${cx},${cy}`);
      }
    }

    // Destruir chunks que salieron de vista.
    for (const chunkKey of this.activeMapChunks) {
      if (!needed.has(chunkKey)) {
        const bucket = this.mapTileChunks.get(chunkKey);
        if (bucket) {
          for (const key of bucket) this.destroyMapTileObject(key);
        }
        this.activeMapChunks.delete(chunkKey);
      }
    }

    // Encolar chunks nuevos. En carga inicial (force) se crean de una; si no, se
    // amortiza por frame en processTileCreateQueue() para no colgar el frame.
    for (const chunkKey of needed) {
      if (this.activeMapChunks.has(chunkKey)) continue;
      const bucket = this.mapTileChunks.get(chunkKey);
      if (bucket) {
        if (force) {
          for (const key of bucket) this.createMapTileObject(key);
        } else {
          for (const key of bucket) this.tileCreateQueue.push(key);
        }
      }
      this.activeMapChunks.add(chunkKey);
    }
  }

  /** Crea hasta MAP_TILES_PER_FRAME tiles de la cola por frame. */
  private processTileCreateQueue(): void {
    if (this.tileCreateQueue.length === 0) return;
    let created = 0;
    const budget = MapEditorScene.MAP_TILES_PER_FRAME;
    while (this.tileCreateQueue.length > 0 && created < budget) {
      const key = this.tileCreateQueue.shift();
      if (key === undefined) break;
      const entry = this.mapTileEntries.get(key);
      if (!entry) continue;
      // Si el chunk salió de vista, descartar.
      if (!this.activeMapChunks.has(this.chunkKeyForTile(entry.tile.x, entry.tile.y))) {
        continue;
      }
      this.createMapTileObject(key);
      created++;
    }
  }

  /** Destruye todos los GameObjects de tile activos y limpia el índice. */
  private clearMapTiles(): void {
    for (const obj of this.activeTileObjects.values()) {
      obj.destroy();
    }
    this.activeTileObjects.clear();
    this.waterSprites.clear();
    this.activeMapChunks.clear();
    this.mapTileEntries.clear();
    this.mapTileChunks.clear();
    this.tileCreateQueue = [];
  }

  /** Arranca (una sola vez) el timer que cicla los frames del agua (en fase). */
  private ensureWaterAnimTimer(): void {
    if (this.waterAnimTimer) return;
    this.waterAnimTimer = this.time.addEvent({
      delay: WATER_FRAME_MS,
      loop: true,
      callback: () => {
        this.waterFrame++;
        for (const { sprite, type } of this.waterSprites.values()) {
          sprite.setTexture(getWaterFrameTextureKey(type, this.waterFrame % getWaterFrameCount(type)));
        }
      },
    });
  }


  /**
   * Calcula una profundidad coherente para un tile de mapa, igual que en GameScene.
   */
  private getDepthForMapLayer(layerName: string, tileY: number, zOverride?: number): number {
    if (typeof zOverride === 'number') {
      return this.getDepthFromTileY(tileY) + zOverride;
    }

    // =========================================================================
    // SISTEMA DE 4 CAPAS (sincronizado con GameScene)
    // Capas 1, 2 y 4 usan DEPTH FIJO para que sprites multi-tile funcionen bien
    // Capa 3 usa DEPTH DINÁMICO para la regla especial de interacción Y
    // =========================================================================
    switch (layerName) {
      // CAPA 1: Suelo base - DEPTH FIJO (siempre atrás)
      case 'ground':
      case 'layer1_ground':
        return -200;

      // CAPA 2: Decoración de piso - DEPTH FIJO (siempre debajo de entidades)
      case 'ground_deco_under':
      case 'layer2_floor_objects':
        return -100;

      // CAPA 3: Decoración con interacción Y (árboles, columnas)
      case 'ground_deco_over':
      case 'layer3_solid':
        return this.getDepthFromTileY(tileY) + 0.4;

      // CAPA 4: Techos - DEPTH FIJO (siempre encima)
      case 'roof':
      case 'layer4_roof':
        return 800;

      default:
        return this.getDepthFromTileY(tileY);
    }
  }

  /**
   * Normaliza la coordenada Y de grilla (0..WORLD_TILES-1) a un rango de depth
   * seguro para el mundo (0..~700), igual que en GameScene.
   */
  private getDepthFromTileY(tileY: number): number {
    const clamped = Phaser.Math.Clamp(tileY, 0, WORLD_TILES - 1);
    const normalized = clamped / WORLD_TILES; // 0..1
    return normalized * 700;
  }
  
  /**
   * Obtiene el índice de superficies cargado.
   * Útil para el panel HTML del editor.
   */
  public getSurfaceIndex(): SurfaceIndex | null {
    return this.surfaceIndex;
  }
  
  /**
   * Obtiene las superficies agrupadas por tipo.
   * Útil para el selector del panel HTML.
   */
  public getSurfacesByType(): Record<string, Array<{ id: string; surface: SurfaceDefinition }>> {
    return getSurfacesByType();
  }
  
  /**
   * Obtiene las superficies agrupadas por tipo y subtipo.
   * Útil para el selector del panel HTML con mejor organización.
   */
  public getSurfacesByTypeAndSubtype(): Record<string, Record<string, Array<{ id: string; surface: SurfaceDefinition }>>> {
    return getSurfacesByTypeAndSubtype();
  }
  
  /**
   * Obtiene el índice de decoraciones cargado.
   */
  public getDecorationIndex(): Record<string, { name: string; type: string }> | null {
    return this.decorationIndex;
  }
  
  /**
   * Actualiza la posición y visibilidad del preview de superficie.
   * El preview se posiciona con el tile del mouse como esquina superior izquierda,
   * y se dibuja hacia abajo y a la derecha.
   */
  private updateSurfacePreview(): void {
    if (!this.selectedSurfaceId || this.previewTileX < 0 || this.previewTileY < 0) {
      // Ocultar preview si no hay superficie seleccionada
      if (this.surfacePreview) {
        this.surfacePreview.setVisible(false);
      }
      return;
    }
    
    // Determinar la clave de textura
    let textureKey = this.selectedSurfaceId;
    const surfaceTextureKey = getSurfaceTextureKey(this.selectedSurfaceId);
    if (this.textures.exists(surfaceTextureKey)) {
      textureKey = surfaceTextureKey;
    }
    
    // Verificar que la textura existe
    if (!this.textures.exists(textureKey)) {
      if (this.surfacePreview) {
        this.surfacePreview.setVisible(false);
      }
      console.warn(`[MapEditorScene] Preview: Textura no encontrada: ${textureKey}`);
      return;
    }
    
    // Posición en la esquina superior izquierda del tile
    // (el mouse indica dónde empieza la superficie, que se expande hacia abajo y derecha)
    const worldX = this.previewTileX * TILE_SIZE;
    const worldY = this.previewTileY * TILE_SIZE;
    
    // Crear o actualizar el preview
    if (!this.surfacePreview) {
      this.surfacePreview = this.add.image(worldX, worldY, textureKey);
      this.surfacePreview.setAlpha(0.5);
      this.surfacePreview.setDepth(9999); // Muy alto para que se vea sobre casi todo
      this.surfacePreview.setOrigin(0, 0); // Origen en esquina superior izquierda
    } else {
      // Actualizar textura si cambió
      if (this.surfacePreview.texture.key !== textureKey) {
        this.surfacePreview.setTexture(textureKey);
      }
      this.surfacePreview.setPosition(worldX, worldY);
      this.surfacePreview.setOrigin(0, 0); // Origen en esquina superior izquierda
      this.surfacePreview.setVisible(true);
    }
  }
  
  /**
   * Establece la superficie seleccionada para previsualización y colocación.
   * @param surfaceId ID de la superficie (o null para deseleccionar)
   * @param layerName Nombre de la capa donde se colocará
   */
  public setSelectedSurface(surfaceId: string | null, layerName: string = 'ground'): void {
    this.selectedSurfaceId = surfaceId;
    this.selectedLayerName = layerName;
    // Elegir una superficie/decoración sale del modo "casa entera".
    if (surfaceId) this.selectedHouseId = null;

    if (!surfaceId) {
      // Ocultar preview
      if (this.surfacePreview) {
        this.surfacePreview.setVisible(false);
      }
      this.previewTileX = -1;
      this.previewTileY = -1;
    } else {
      // Lazy-load de la textura de decoración si todavía no está cargada (decoraciones
      // que no están colocadas en el mapa —ej. casas nuevas— no las precarga
      // preloadMapAssets). Sin esto, el preview/colocación no encontraría la textura.
      if (isDecorationKey(surfaceId) && !this.textures.exists(surfaceId)) {
        this.load.image(surfaceId, getDecorationAssetUrl(surfaceId));
        this.load.once('complete', () => {
          if (this.selectedSurfaceId === surfaceId && this.previewTileX >= 0 && this.previewTileY >= 0) {
            this.updateSurfacePreview();
          }
        });
        this.load.start();
      }
      // Actualizar preview si ya tenemos una posición
      if (this.previewTileX >= 0 && this.previewTileY >= 0) {
        this.updateSurfacePreview();
      }
    }
  }
  
  /**
   * Obtiene la superficie actualmente seleccionada.
   */
  public getSelectedSurface(): { surfaceId: string | null; layerName: string } {
    return {
      surfaceId: this.selectedSurfaceId,
      layerName: this.selectedLayerName,
    };
  }

  /**
   * Entra en modo "estampar casa entera": al clickear se coloca la casa completa
   * (todas sus partes en sus capas: cuerpo en `ground_deco_over`, techo en `roof`)
   * más la colisión de su huella, de una. Precarga las texturas de todas las partes.
   * @param houseId id de `HOUSES` (o null para salir del modo).
   */
  public setSelectedHouse(houseId: string | null): void {
    this.selectedHouseId = houseId;
    if (houseId) {
      this.selectedSurfaceId = null; // mutuamente excluyente con superficie/decoración
      const def = HOUSES[houseId];
      if (def) {
        // Lazy-load de las texturas de todas las partes (igual que las decoraciones
        // sueltas: si la casa no está en el mapa, preloadMapAssets no las cargó).
        let pending = false;
        for (const part of def.parts) {
          if (!this.textures.exists(part.key)) {
            this.load.image(part.key, getDecorationAssetUrl(part.key));
            pending = true;
          }
        }
        if (pending) {
          this.load.once('complete', () => {
            if (this.selectedHouseId === houseId && this.previewTileX >= 0 && this.previewTileY >= 0) {
              this.updateHousePreview();
            }
          });
          this.load.start();
        }
      }
      if (this.previewTileX >= 0 && this.previewTileY >= 0) {
        this.updateHousePreview();
      }
    } else if (this.surfacePreview) {
      this.surfacePreview.setVisible(false);
    }
  }

  /**
   * Preview fantasma de la casa: muestra la parte de adelante (tilesBack 0) anclada en
   * el cursor, para ubicar dónde va la base de la casa.
   */
  private updateHousePreview(): void {
    const def = this.selectedHouseId ? HOUSES[this.selectedHouseId] : null;
    if (!def || this.previewTileX < 0 || this.previewTileY < 0) {
      if (this.surfacePreview) this.surfacePreview.setVisible(false);
      return;
    }
    const front = def.parts.find((p) => p.layer !== 'roof') ?? def.parts[0];
    if (!front || !this.textures.exists(front.key)) {
      if (this.surfacePreview) this.surfacePreview.setVisible(false);
      return;
    }
    // Decoración: ancla inferior-centro en el tile del cursor (origin 0.5, 1).
    const worldX = this.previewTileX * TILE_SIZE + TILE_SIZE / 2;
    const worldY = this.previewTileY * TILE_SIZE + TILE_SIZE;
    if (!this.surfacePreview) {
      this.surfacePreview = this.add.image(worldX, worldY, front.key);
      this.surfacePreview.setAlpha(0.5);
      this.surfacePreview.setDepth(9999);
    } else if (this.surfacePreview.texture.key !== front.key) {
      this.surfacePreview.setTexture(front.key);
    }
    this.surfacePreview.setOrigin(0.5, 1);
    this.surfacePreview.setPosition(worldX, worldY);
    this.surfacePreview.setVisible(true);
  }

  /**
   * Estampa la casa seleccionada en la posición del preview: el tile clickeado es la
   * base (front-centro) de la casa. Coloca cuerpo, techo y colisión de la huella.
   * @returns true si se estampó.
   */
  public placeSelectedHouse(): boolean {
    const houseId = this.selectedHouseId;
    if (!houseId || this.previewTileX < 0 || this.previewTileY < 0) return false;
    const def = HOUSES[houseId];
    if (!def) return false;

    const stamp = stampHouse(this.previewTileX, this.previewTileY, def);
    for (const t of stamp.deco) this.placeTile(t.x, t.y, t.sprite, 'ground_deco_over');
    for (const t of stamp.roof) this.placeTile(t.x, t.y, t.sprite, 'roof');
    // Colisión: bloquear solo los tiles de `blocked` (excluye la entrada, que queda
    // caminable). Toggle solo si no estaba ya bloqueado.
    for (const t of stamp.blocked) {
      const key = `${t.x},${t.y}`;
      if (this.blockedTiles && !this.blockedTiles.has(key)) {
        this.toggleTileBlocked(t.x, t.y);
      }
    }
    this.updateBlockedTilesWindow();
    return true;
  }
  
  /**
   * Coloca la superficie seleccionada en la posición del preview.
   * @returns true si se colocó correctamente
   */
  public placeSelectedSurface(): boolean {
    if (!this.selectedSurfaceId || this.previewTileX < 0 || this.previewTileY < 0) {
      return false;
    }
    
    return this.placeTile(this.previewTileX, this.previewTileY, this.selectedSurfaceId, this.selectedLayerName);
  }

  /**
   * Coloca una superficie/tile en el mapa en la posición y capa especificadas.
   * Si ya existe un tile en esa posición y capa, lo reemplaza.
   * 
   * @param x Coordenada X del tile
   * @param y Coordenada Y del tile
   * @param surfaceId ID de la superficie a colocar
   * @param layerName Nombre de la capa ('ground', 'ground_deco_under', 'ground_deco_over', 'roof')
   * @returns true si se colocó correctamente, false si hubo error
   */
  public placeTile(x: number, y: number, surfaceId: string, layerName: string): boolean {
    if (!this.currentMap) return false;
    
    // Buscar o crear la capa
    let layer = this.currentMap.layers.find(l => l.name === layerName);
    if (!layer) {
      // Crear la capa si no existe
      // Nuevo sistema de capas estilo AO clásico
      const zMap: Record<string, number> = {
        // Nuevas capas (sistema AO clásico)
        'layer1_ground': 0,        // Suelo base
        'layer2_floor_objects': 1, // Objetos bajos (alfombras, caminos)
        'layer3_solid': 2,         // Objetos sólidos/bloqueantes
        'layer4_roof': 3,          // Sobre el personaje (techos)
        // Capas legacy para compatibilidad
        'ground': 0,
        'ground_deco_under': 1,
        'ground_deco_over': 2,
        'roof': 3,
      };
      layer = {
        name: layerName,
        z: zMap[layerName] ?? 0,
        tiles: [],
      };
      this.currentMap.layers.push(layer);
    }
    
    // Guardar estado anterior para el historial
    const existingTile = layer.tiles.find(t => t.x === x && t.y === y);
    const previousSpriteId = existingTile ? existingTile.sprite : null;
    
    // Buscar si ya existe un tile en esa posición
    const existingIndex = layer.tiles.findIndex(t => t.x === x && t.y === y);
    let tile: TileData;
    if (existingIndex >= 0) {
      // Reemplazar el tile existente
      layer.tiles[existingIndex].sprite = surfaceId;
      tile = layer.tiles[existingIndex];
    } else {
      // Agregar nuevo tile
      tile = { x, y, sprite: surfaceId };
      layer.tiles.push(tile);
    }

    // Agregar al historial
    this.addToHistory({
      type: 'place',
      x,
      y,
      layerName,
      previousSpriteId,
      newSpriteId: surfaceId,
    });

    // Sincronizar el GameObject (índice de culling + sprite si el chunk es visible)
    this.upsertTileObject(tile, layerName);

    return true;
  }
  
  /**
   * Elimina un tile de una posición y capa específicas.
   * 
   * @param x Coordenada X del tile
   * @param y Coordenada Y del tile
   * @param layerName Nombre de la capa
   * @returns true si se eliminó, false si no existía
   */
  public removeTile(x: number, y: number, layerName: string): boolean {
    if (!this.currentMap) return false;
    
    const layer = this.currentMap.layers.find(l => l.name === layerName);
    if (!layer) return false;
    
    const index = layer.tiles.findIndex(t => t.x === x && t.y === y);
    if (index < 0) return false;
    
    // Guardar estado anterior para el historial
    const previousSpriteId = layer.tiles[index].sprite;
    
    // Eliminar el tile del array
    layer.tiles.splice(index, 1);

    // Agregar al historial
    this.addToHistory({
      type: 'remove',
      x,
      y,
      layerName,
      previousSpriteId,
      newSpriteId: null,
    });

    // Sacar el tile del índice de culling y destruir su sprite si estaba activo.
    this.dropTileObject(layerName, x, y);

    return true;
  }
  
  /**
   * Sincroniza el GameObject de un tile recién colocado/modificado con el índice
   * de culling. Indexa la entry y, si su chunk está visible, (re)crea el sprite.
   * Reemplaza el viejo `renderTileAt` (que escaneaba `children.list` en O(n)).
   */
  private upsertTileObject(tile: TileData, layerName: string): void {
    const key = this.tileEntryKey(layerName, tile.x, tile.y);
    this.indexTileEntry(tile, layerName);
    // Recrear el objeto (la textura pudo cambiar respecto al que hubiera).
    this.destroyMapTileObject(key);
    if (this.activeMapChunks.has(this.chunkKeyForTile(tile.x, tile.y))) {
      this.createMapTileObject(key);
    }
    MapEditorScene.minimapDirty = true;
  }

  /** Saca un tile del índice de culling y destruye su sprite si estaba activo. */
  private dropTileObject(layerName: string, x: number, y: number): void {
    const key = this.tileEntryKey(layerName, x, y);
    this.destroyMapTileObject(key);
    this.unindexTileEntry(layerName, x, y);
    MapEditorScene.minimapDirty = true;
  }
  
  /**
   * Obtiene el mapa actual (para exportar JSON).
   */
  public getCurrentMap(): MapData | null {
    return this.currentMap;
  }
  
  /**
   * Agrega una acción al historial para poder hacer undo.
   */
  private addToHistory(action: {
    type: 'place' | 'remove';
    x: number;
    y: number;
    layerName: string;
    previousSpriteId: string | null;
    newSpriteId: string | null;
  }): void {
    this.actionHistory.push(action);
    
    // Limitar el tamaño del historial
    if (this.actionHistory.length > this.maxHistorySize) {
      this.actionHistory.shift(); // Eliminar la acción más antigua
    }
  }
  
  /**
   * Revierte la última acción (undo).
   */
  public undo(): boolean {
    if (this.actionHistory.length === 0 || !this.currentMap) {
      return false;
    }
    
    const lastAction = this.actionHistory.pop()!;
    
    // Buscar o crear la capa
    let layer = this.currentMap.layers.find(l => l.name === lastAction.layerName);
    if (!layer) {
      // Si la capa no existe y estamos revirtiendo una eliminación, no hay nada que hacer
      if (lastAction.type === 'remove') {
        return false;
      }
      // Si estamos revirtiendo una colocación y la capa no existe, simplemente retornar
      return false;
    }
    
    if (lastAction.type === 'place') {
      // Revertir colocación: restaurar estado anterior
      if (lastAction.previousSpriteId === null) {
        // No había nada antes, eliminar el tile
        const index = layer.tiles.findIndex(t => t.x === lastAction.x && t.y === lastAction.y);
        if (index >= 0) {
          layer.tiles.splice(index, 1);
        }

        // Sacar del índice + destruir el sprite si estaba activo.
        this.dropTileObject(lastAction.layerName, lastAction.x, lastAction.y);
      } else {
        // Había algo antes, restaurarlo
        const existingIndex = layer.tiles.findIndex(t => t.x === lastAction.x && t.y === lastAction.y);
        let tile: TileData;
        if (existingIndex >= 0) {
          layer.tiles[existingIndex].sprite = lastAction.previousSpriteId;
          tile = layer.tiles[existingIndex];
        } else {
          tile = { x: lastAction.x, y: lastAction.y, sprite: lastAction.previousSpriteId };
          layer.tiles.push(tile);
        }

        // Re-sincronizar con el sprite anterior
        this.upsertTileObject(tile, lastAction.layerName);
      }
    } else if (lastAction.type === 'remove') {
      // Revertir eliminación: restaurar el sprite que había
      if (lastAction.previousSpriteId !== null) {
        // Agregar el tile de vuelta
        const existingIndex = layer.tiles.findIndex(t => t.x === lastAction.x && t.y === lastAction.y);
        let tile: TileData;
        if (existingIndex >= 0) {
          layer.tiles[existingIndex].sprite = lastAction.previousSpriteId;
          tile = layer.tiles[existingIndex];
        } else {
          tile = { x: lastAction.x, y: lastAction.y, sprite: lastAction.previousSpriteId };
          layer.tiles.push(tile);
        }

        // Re-sincronizar
        this.upsertTileObject(tile, lastAction.layerName);
      }
    }
    
    return true;
  }
  
  // =========================================================================
  // LIGHT MANAGEMENT
  // =========================================================================
  
  /**
   * Inicializa las luces desde el mapa o desde lightPlacements estático.
   */
  private initializeLights(): void {
    // Cargar luces del mapa si existen
    if (this.currentMap?.lights) {
      MapEditorScene.lightPlacements = [...this.currentMap.lights];
    }

    // Renderizar las luces
    this.renderLights();
    // Marcadores de deco-objects (fogatas, etc.).
    this.renderDecoMarkers();
  }
  
  /**
   * Renderiza todas las luces como círculos semitransparentes.
   */
  private renderLights(): void {
    // Limpiar sprites anteriores
    this.lightSprites.forEach((obj) => {
      obj.circle.destroy();
      obj.label.destroy();
    });
    this.lightSprites.clear();
    
    if (!this.showLights) return;
    
    for (const light of MapEditorScene.lightPlacements) {
      this.renderLightAt(light);
    }
  }
  
  /**
   * Renderiza una luz individual.
   */
  private renderLightAt(light: MapLightData): void {
    const worldX = light.x * TILE_SIZE + TILE_SIZE / 2;
    const worldY = light.y * TILE_SIZE + TILE_SIZE / 2;
    
    // Parsear el color
    const colorValue = typeof light.color === 'string'
      ? parseInt(light.color.replace('0x', ''), 16)
      : light.color;
    
    // Crear círculo para visualizar el radio de la luz
    const circle = this.add.arc(worldX, worldY, light.radius, 0, 360, false, colorValue, 0.2);
    circle.setStrokeStyle(2, colorValue, 0.8);
    circle.setDepth(9000); // Alto para que se vea sobre el mapa
    
    // Etiqueta con el ID de la luz
    const label = this.add.text(worldX, worldY - 10, light.id, {
      fontSize: '10px',
      color: '#ffffff',
      backgroundColor: 'rgba(0,0,0,0.7)',
      padding: { x: 2, y: 1 },
    })
    .setOrigin(0.5)
    .setDepth(9001);
    
    this.lightSprites.set(light.id, { circle, label });
  }
  
  /**
   * Agrega una luz al mapa.
   */
  public addLight(light: MapLightData): void {
    // Verificar que no exista una luz con el mismo ID
    const existingIndex = MapEditorScene.lightPlacements.findIndex(l => l.id === light.id);
    if (existingIndex >= 0) {
      // Reemplazar la existente
      MapEditorScene.lightPlacements[existingIndex] = light;
      // Destruir el sprite anterior
      const existing = this.lightSprites.get(light.id);
      if (existing) {
        existing.circle.destroy();
        existing.label.destroy();
        this.lightSprites.delete(light.id);
      }
    } else {
      MapEditorScene.lightPlacements.push(light);
    }
    
    // Renderizar la nueva luz
    this.renderLightAt(light);
    
    // Actualizar el mapa
    if (this.currentMap) {
      this.currentMap.lights = [...MapEditorScene.lightPlacements];
    }
  }
  
  /**
   * Elimina una luz del mapa por su ID.
   */
  public removeLight(lightId: string): boolean {
    const index = MapEditorScene.lightPlacements.findIndex(l => l.id === lightId);
    if (index < 0) return false;
    
    MapEditorScene.lightPlacements.splice(index, 1);
    
    // Destruir el sprite
    const sprite = this.lightSprites.get(lightId);
    if (sprite) {
      sprite.circle.destroy();
      sprite.label.destroy();
      this.lightSprites.delete(lightId);
    }
    
    // Actualizar el mapa
    if (this.currentMap) {
      this.currentMap.lights = [...MapEditorScene.lightPlacements];
    }
    
    return true;
  }
  
  /**
   * Obtiene la luz en una posición específica (si existe).
   */
  public getLightAt(x: number, y: number): MapLightData | null {
    return MapEditorScene.lightPlacements.find(l => l.x === x && l.y === y) || null;
  }
  
  /**
   * Dibuja un marcador de deco-object (fogata, etc.) en el editor: un rombo/círculo naranja + label.
   * Sólo visual del editor (el render real in-game lo hace MapLoader.renderDecoObjects).
   */
  public addDecoMarker(id: string, x: number, y: number, color: string): void {
    this.removeDecoMarker(id);
    const worldX = x * TILE_SIZE + TILE_SIZE / 2;
    const worldY = y * TILE_SIZE + TILE_SIZE / 2;
    const colorValue = parseInt(color.replace('#', '').replace('0x', ''), 16);
    const marker = this.add.arc(worldX, worldY, 10, 0, 360, false, colorValue, 0.9).setDepth(9002);
    marker.setStrokeStyle(2, 0xffffff, 0.9);
    const label = this.add
      .text(worldX, worldY - 14, '🔥', { fontSize: '12px' })
      .setOrigin(0.5)
      .setDepth(9003);
    this.decoMarkers.set(id, { marker, label });
  }

  /** Elimina el marcador de deco-object del editor. */
  public removeDecoMarker(id: string): void {
    const m = this.decoMarkers.get(id);
    if (m) {
      m.marker.destroy();
      m.label.destroy();
      this.decoMarkers.delete(id);
    }
  }

  /** Dibuja los marcadores de todos los deco-objects del mapa actual (al cargar). */
  public renderDecoMarkers(): void {
    for (const m of this.decoMarkers.values()) {
      m.marker.destroy();
      m.label.destroy();
    }
    this.decoMarkers.clear();
    for (const deco of this.currentMap?.decoObjects ?? []) {
      this.addDecoMarker(deco.id, deco.x, deco.y, '#FF7A2A');
    }
  }

  /**
   * Obtiene todas las luces del mapa.
   */
  public getLights(): MapLightData[] {
    return [...MapEditorScene.lightPlacements];
  }
  
  /**
   * Activa/desactiva la visualización de luces.
   */
  public setShowLights(show: boolean): void {
    this.showLights = show;
    this.renderLights();
  }
  
  /**
   * Activa el modo de colocación de luces con la configuración dada.
   */
  public setLightPlacementMode(config: Omit<MapLightData, 'id' | 'x' | 'y'> | null): void {
    this.lightPlacementMode = config !== null;
    this.pendingLightConfig = config;
    
    // Deseleccionar superficie si estamos en modo luz
    if (this.lightPlacementMode && this.selectedSurfaceId) {
      this.setSelectedSurface(null);
    }
  }
  
  /**
   * Genera un ID único para una luz.
   */
  private generateLightId(): string {
    const timestamp = Date.now().toString(36);
    const random = Math.random().toString(36).substring(2, 6);
    return `light_${timestamp}_${random}`;
  }
  
  /**
   * Coloca una luz en la posición del tile seleccionado usando la configuración pendiente.
   */
  public placeLightAtSelectedTile(): boolean {
    if (!this.pendingLightConfig) return false;

    const light: MapLightData = {
      id: this.generateLightId(),
      x: this.selectedTileX,
      y: this.selectedTileY,
      ...this.pendingLightConfig,
    };

    this.addLight(light);
    return true;
  }

  // =========================================================================
  // TRANSLADO MANAGEMENT
  // =========================================================================

  private initializeTranslados(): void {
    if (this.currentMap?.translados) {
      MapEditorScene.transladoPlacements = [...this.currentMap.translados];
    } else if (!this.currentMap?.translados) {
      MapEditorScene.transladoPlacements = [];
    }
    this.renderTranslados();
  }

  private renderTranslados(): void {
    this.transladoSprites.forEach((obj) => {
      obj.ring.destroy();
      obj.label.destroy();
      obj.sprite?.destroy();
    });
    this.transladoSprites.clear();

    if (!this.showTranslados) return;
    for (const t of MapEditorScene.transladoPlacements) {
      this.renderTransladoAt(t);
    }
  }

  /**
   * Renderiza un translado: anillo amarillo + label con destino + (opcional) sprite de la textura elegida.
   */
  public renderTransladoAt(t: MapTransladoData): void {
    const worldX = t.x * TILE_SIZE + TILE_SIZE / 2;
    const worldY = t.y * TILE_SIZE + TILE_SIZE / 2;
    const ring = this.add.arc(worldX, worldY, TILE_SIZE * 0.45, 0, 360, false, 0xffd700, 0.0);
    ring.setStrokeStyle(2, 0xffd700, 0.9);
    ring.setDepth(9100);

    const labelText = `→ M${t.destMapId} (${t.destX},${t.destY})${t.randomizeArrival ? ' 🎲' : ''}`;
    const label = this.add
      .text(worldX, worldY - 14, labelText, {
        fontSize: '9px',
        color: '#000',
        backgroundColor: '#ffd700',
        padding: { x: 3, y: 1 },
      })
      .setOrigin(0.5)
      .setDepth(9101);

    let sprite: Phaser.GameObjects.Image | null = null;
    if (t.sprite) {
      let textureKey = t.sprite;
      const surfaceTextureKey = getSurfaceTextureKey(t.sprite);
      if (this.textures.exists(surfaceTextureKey)) textureKey = surfaceTextureKey;
      if (this.textures.exists(textureKey)) {
        sprite = this.add.image(worldX, worldY, textureKey);
        sprite.setOrigin(0.5, 0.5);
        sprite.setDepth(9099); // debajo del ring pero sobre el suelo
        sprite.setAlpha(0.9);
      }
    }

    this.transladoSprites.set(t.id, { ring, label, sprite });
  }

  public addTranslado(t: MapTransladoData): void {
    const existingIndex = MapEditorScene.transladoPlacements.findIndex((x) => x.id === t.id);
    if (existingIndex >= 0) {
      MapEditorScene.transladoPlacements[existingIndex] = t;
      const existing = this.transladoSprites.get(t.id);
      if (existing) {
        existing.ring.destroy();
        existing.label.destroy();
        existing.sprite?.destroy();
        this.transladoSprites.delete(t.id);
      }
    } else {
      MapEditorScene.transladoPlacements.push(t);
    }
    this.renderTransladoAt(t);
    if (this.currentMap) {
      this.currentMap.translados = [...MapEditorScene.transladoPlacements];
    }
  }

  public removeTranslado(transladoId: string): boolean {
    const index = MapEditorScene.transladoPlacements.findIndex((t) => t.id === transladoId);
    if (index < 0) return false;
    MapEditorScene.transladoPlacements.splice(index, 1);
    const sprite = this.transladoSprites.get(transladoId);
    if (sprite) {
      sprite.ring.destroy();
      sprite.label.destroy();
      sprite.sprite?.destroy();
      this.transladoSprites.delete(transladoId);
    }
    if (this.currentMap) {
      this.currentMap.translados = [...MapEditorScene.transladoPlacements];
    }
    return true;
  }

  public getTransladoAt(x: number, y: number): MapTransladoData | null {
    return MapEditorScene.transladoPlacements.find((t) => t.x === x && t.y === y) ?? null;
  }

  public getTranslados(): MapTransladoData[] {
    return [...MapEditorScene.transladoPlacements];
  }

  public setShowTranslados(show: boolean): void {
    this.showTranslados = show;
    this.renderTranslados();
  }

  // =========================================================================
  // ÁREA MANAGEMENT
  // =========================================================================

  private initializeAreas(): void {
    MapEditorScene.areaPlacements = this.currentMap?.areas ? [...this.currentMap.areas] : [];
    this.renderAreas();
  }

  // ── Cardúmenes de pesca (áreas de agua pescables) ──
  private initializeFishingSpots(): void {
    MapEditorScene.fishingSpotPlacements = this.currentMap?.fishingSpots
      ? [...this.currentMap.fishingSpots]
      : [];
    this.renderFishingSpots();
  }

  private renderFishingSpots(): void {
    this.fishSpotSprites.forEach((o) => {
      o.rect.destroy();
      o.label.destroy();
    });
    this.fishSpotSprites.clear();
    if (!this.showFishingSpots) return;
    const color = 0x2ec5ff;
    for (const spot of MapEditorScene.fishingSpotPlacements) {
      const r = spot.rect;
      const rect = this.add
        .rectangle(r.x * TILE_SIZE, r.y * TILE_SIZE, r.w * TILE_SIZE, r.h * TILE_SIZE, color, 0.16)
        .setOrigin(0, 0)
        .setStrokeStyle(2, color, 0.9)
        .setDepth(8520);
      const sp = FISH_SPECIES.find((f) => `fish_node_${f.species}` === spot.typeId);
      const label = this.add
        .text((r.x + r.w / 2) * TILE_SIZE, (r.y + r.h / 2) * TILE_SIZE, `🎣 ${sp?.label ?? spot.typeId}`, {
          fontSize: '12px',
          color: '#ffffff',
          backgroundColor: 'rgba(0,40,60,0.6)',
          padding: { x: 3, y: 1 },
        })
        .setOrigin(0.5)
        .setDepth(8600);
      this.fishSpotSprites.set(spot.id, { rect, label });
    }
  }

  /** Reemplaza la lista de cardúmenes y re-renderiza (llamado por el editor al editar). */
  public updateFishingSpots(spots: MapFishingSpot[]): void {
    MapEditorScene.fishingSpotPlacements = spots;
    if (this.currentMap) this.currentMap.fishingSpots = spots.length > 0 ? [...spots] : undefined;
    this.renderFishingSpots();
  }

  public getFishingSpots(): MapFishingSpot[] {
    return [...MapEditorScene.fishingSpotPlacements];
  }

  /** Color determinista por id de área (hue estable). */
  private areaColor(id: string): number {
    let h = 0;
    for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
    const hue = h % 360;
    const color = Phaser.Display.Color.HSVToRGB(hue / 360, 0.7, 1) as Phaser.Types.Display.ColorObject;
    return Phaser.Display.Color.GetColor(color.r, color.g, color.b);
  }

  private renderAreas(): void {
    this.areaSprites.forEach((obj) => {
      obj.rects.forEach((r) => r.destroy());
      obj.zoneRects.forEach((r) => r.destroy());
      obj.label?.destroy();
    });
    this.areaSprites.clear();

    if (!this.showAreas) return;
    for (const area of MapEditorScene.areaPlacements) {
      this.renderAreaAt(area);
    }
  }

  public renderAreaAt(area: MapAreaData): void {
    const color = this.areaColor(area.id);
    const rects: Phaser.GameObjects.Rectangle[] = [];
    const zoneRects: Phaser.GameObjects.Rectangle[] = [];

    for (const r of area.rects) {
      const rect = this.add
        .rectangle(r.x * TILE_SIZE, r.y * TILE_SIZE, r.w * TILE_SIZE, r.h * TILE_SIZE, color, 0.18)
        .setOrigin(0, 0)
        .setStrokeStyle(2, color, 0.9)
        .setDepth(8500);
      rects.push(rect);
    }

    // Zonas de respawn: verde, sobre el área.
    const zoneColor = 0x33ff66;
    for (const zone of area.respawnZones ?? []) {
      for (const r of zone.rects) {
        const zr = this.add
          .rectangle(r.x * TILE_SIZE, r.y * TILE_SIZE, r.w * TILE_SIZE, r.h * TILE_SIZE, zoneColor, 0.25)
          .setOrigin(0, 0)
          .setStrokeStyle(1, zoneColor, 0.9)
          .setDepth(8550);
        zoneRects.push(zr);
      }
    }

    // Label con el nombre, centrado en el primer rect.
    let label: Phaser.GameObjects.Text | null = null;
    if (area.name && area.rects.length > 0) {
      const r0 = area.rects[0];
      const cx = (r0.x + r0.w / 2) * TILE_SIZE;
      const cy = (r0.y + r0.h / 2) * TILE_SIZE;
      label = this.add
        .text(cx, cy, area.name, {
          fontSize: '12px',
          color: '#ffffff',
          backgroundColor: 'rgba(0,0,0,0.6)',
          padding: { x: 3, y: 1 },
        })
        .setOrigin(0.5)
        .setDepth(8600);
    }

    this.areaSprites.set(area.id, { rects, zoneRects, label });
  }

  /** Reemplaza la lista de áreas y re-renderiza (llamado por AuthUI al editar). */
  public updateAreas(areas: MapAreaData[]): void {
    MapEditorScene.areaPlacements = areas;
    if (this.currentMap) {
      this.currentMap.areas = areas.length > 0 ? [...areas] : undefined;
    }
    this.renderAreas();
  }

  public getAreas(): MapAreaData[] {
    return [...MapEditorScene.areaPlacements];
  }

  public setShowAreas(show: boolean): void {
    this.showAreas = show;
    this.renderAreas();
  }

  /** Activa/desactiva el modo de dibujo de rectángulos de área. */
  public setAreaDrawMode(on: boolean): void {
    this.areaDrawMode = on;
    if (on) {
      // Mutuamente excluyente con superficie y luz.
      if (this.selectedSurfaceId) this.setSelectedSurface(null);
      this.lightPlacementMode = false;
      this.pendingLightConfig = null;
    } else {
      this.areaDrawStart = null;
      this.areaDrawCurrent = null;
      if (this.areaDrawPreview) { this.areaDrawPreview.destroy(); this.areaDrawPreview = null; }
    }
  }

  public isAreaDrawMode(): boolean {
    return this.areaDrawMode;
  }

  private updateAreaDrawPreview(): void {
    if (!this.areaDrawStart || !this.areaDrawCurrent) return;
    const sx = Math.min(this.areaDrawStart.x, this.areaDrawCurrent.x);
    const sy = Math.min(this.areaDrawStart.y, this.areaDrawCurrent.y);
    const ex = Math.max(this.areaDrawStart.x, this.areaDrawCurrent.x);
    const ey = Math.max(this.areaDrawStart.y, this.areaDrawCurrent.y);
    const px = sx * TILE_SIZE;
    const py = sy * TILE_SIZE;
    const w = (ex - sx + 1) * TILE_SIZE;
    const h = (ey - sy + 1) * TILE_SIZE;
    if (!this.areaDrawPreview) {
      this.areaDrawPreview = this.add
        .rectangle(px, py, w, h, 0xffd700, 0.25)
        .setOrigin(0, 0)
        .setStrokeStyle(2, 0xffd700, 1)
        .setDepth(8700);
    } else {
      this.areaDrawPreview.setPosition(px, py);
      this.areaDrawPreview.setSize(w, h);
      this.areaDrawPreview.setVisible(true);
    }
  }
}

